import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Dialog,
  useMediaQuery,
} from "@mui/material";
import Tile from "../../types/tile";
import { useDispatch, useSelector } from "../../store";
import { useNavigate } from "react-router-dom";
import {
  deleteTile,
  getBackgroundUrl,
  getTile,
  selectTile,
} from "../../slices/tile";
import {
  getDashboardDetail,
  setCurrentDashboard,
  setSelectedDashboard,
} from "../../slices/dashboard";
import { Edit, Delete, ArrowBack } from "@mui/icons-material";
import Button from "./Button";
import DeleteConfirmation from "./DeleteConfirmation";
import theme from "../../theme";

interface Props {
  tile: Tile;
}

const DashboardTile: React.FC<Props> = (props) => {
  const { tile } = props;
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const md = useMediaQuery(theme.breakpoints.up("md"));

  const { homeDashboard, currentDashboard } = useSelector(
    (store) => store.dashboard
  );
  const { editMode } = useSelector((store) => store.shared);
  const { status } = useSelector((store) => store.tiles);

  const [imgPath, setImgPath] = useState<string>("");
  const [dashboardTile, setDashboardTile] = useState<Tile>(tile);
  const [dialogOpen, setDialogOpen] = useState<{
    id?: number;
    type: "delete" | "none";
  }>({ id: undefined, type: "none" });

  const changeDashboard = (id: number) => {
    dispatch(setCurrentDashboard(undefined));

    if (id >= homeDashboard?.id!) {
      navigate(`/dashboard/${id}`);
    } else {
      navigate(`/dashboard/${homeDashboard?.id}`);
    }
  };

  const handleClick = () => {
    if (!editMode && dashboardTile?.childDashboard !== currentDashboard?.id) {
      if (dashboardTile?.childDashboard! >= homeDashboard?.id!) {
        changeDashboard(dashboardTile?.childDashboard!);
      } else if (
        !!dashboardTile?.parentDashboard! &&
        dashboardTile?.parentDashboard! !== currentDashboard?.id &&
        dashboardTile.parentDashboard >= homeDashboard?.id!
      ) {
        changeDashboard(dashboardTile?.parentDashboard!);
      }
    }
  };

  const handleOpenDialog = (type: "delete" | "none") => {
    setDialogOpen({ id: dashboardTile?.id!, type: type });
  };

  const handleCloseDialog = () => {
    setDialogOpen({ id: undefined, type: "none" });
  };

  const handleDelete = async () => {
    await dispatch(deleteTile(dialogOpen.id!));
    await dispatch(getDashboardDetail(currentDashboard?.id!));
    handleCloseDialog();
  };

  useEffect(() => {
    const getTileData = async () => {
      if (tile?.id! > 0) {
        await dispatch(
          getTile({ id: tile?.id!, includeData: false, fullTile: false })
        ).then((tileData) => {
          if (tileData && tileData.payload) {
            setDashboardTile(tileData.payload!);
          }
        });
      }
    };

    getTileData();
  }, [dispatch, tile?.id]);

  useEffect(() => {
    const getBackgroundImg = async () => {
      if (tile.backgroundId) {
        const img = await dispatch(getBackgroundUrl(tile.backgroundId));
        if (img) {
          setImgPath(img.payload);
        }
      }
    };

    getBackgroundImg();
  }, [dispatch, tile.backgroundId]);

  return (
    <>
      <Box
        position="relative"
        component="img"
        src={
          imgPath ||
          `https://picsum.photos/seed/${tile?.backgroundId! + 19}/300`
        }
        alt={`background`}
      />

      <Box
        onClick={handleClick}
        position="absolute"
        width="100%"
        height="100%"
        display="flex"
        flexDirection="column"
        gap={2}
        justifyContent="center" // vertical center
        alignItems="center" // horizontal center
        textAlign="center"
        sx={{
          backgroundColor: "rgba(0,0,0,.5)",
          cursor: !editMode ? "pointer" : "",
        }}
        color="#fff"
      >
        {!!dashboardTile ? (
          <Typography color="#fff" variant={md ? "h2" : "h3"}>
            {dashboardTile?.title}
          </Typography>
        ) : (
          <CircularProgress size="80px" color="inherit" />
        )}
        {!!currentDashboard &&
        currentDashboard.id > 1 &&
        currentDashboard.id > homeDashboard?.id! &&
        !!dashboardTile?.parentDashboard &&
        dashboardTile?.parentDashboard !== currentDashboard?.id &&
        dashboardTile.childDashboard === null &&
        !editMode ? (
          <Box width="100%" position="absolute" bottom={md ? 50 : 30}>
            <Button
              variant="contained"
              label="Back"
              icon={<ArrowBack />}
              color="info"
              onClick={() => {
                changeDashboard(dashboardTile?.parentDashboard!);
              }}
            />
          </Box>
        ) : (
          !!editMode &&
          !!dashboardTile &&
          dashboardTile.childDashboard !== null && (
            <Box width="100%" position="absolute" bottom={35}>
              <Box
                display="flex"
                flexDirection="row"
                justifyContent="center"
                color="#fff"
              >
                <Button
                  variant="contained"
                  onClick={() => {
                    dispatch(selectTile(dashboardTile));
                    dispatch(
                      setSelectedDashboard(dashboardTile?.dashboard?.id)
                    );
                    navigate(`/edit-dashboard/${dashboardTile?.id}`);
                  }}
                  disabled={false}
                  color="inherit"
                  tooltip="Edit Dashboard"
                  icon={<Edit color="inherit" />}
                />
                <Button
                  variant="contained"
                  onClick={() => handleOpenDialog("delete")}
                  disabled={false}
                  color="inherit"
                  tooltip="Delete Tile"
                  icon={<Delete />}
                />
              </Box>
              <Dialog open={dialogOpen.type === "delete"}>
                <DeleteConfirmation
                  name="this tile"
                  extraInfo="Deleting this tile will delete the dashboard and all the tiles associated with it."
                  handleClose={handleCloseDialog}
                  handleDelete={handleDelete}
                  loading={status === "loading"}
                />
              </Dialog>
            </Box>
          )
        )}
      </Box>
    </>
  );
};

export default DashboardTile;
